import React, { useEffect, useMemo, useRef, useState } from "react";
import useEscapeLayer from "../useEscape";
import { shortDate } from "./format.mjs";
import { transcriptClock, turnIndexAt, matchingTurns } from "./lead/leadModel.mjs";

/*
 * A RECORDED CALL'S TRANSCRIPT, READ IN THE CRM (asked 2026-10-13: "The
 * transcript link should open the transcript within the CRM - not take me to
 * Fireflies").
 *
 * Over the record, in the confirm's card grammar (black rule, sentence-case
 * title, × and Esc to close, one layer at a time through useEscape). The words
 * come from GET /activities/:id/transcript, which fetches them from Fireflies the
 * first time anyone opens the call and keeps them (src/calls/transcript.js).
 *
 * Opened from a promise's time on the call card, it lands on that line and marks
 * it, so the quote behind "Send the proposal" is one click from the promise.
 * A find box narrows nothing: it marks the lines that match and steps through
 * them, because the lines around a match are the reason to read a transcript.
 *
 * Props:
 *   activityId  the call's activity id
 *   at          seconds from the start to open at, or null for the top
 *   ops         ops.call(path)
 *   onClose     () => void
 */
export default function TranscriptDialog({ activityId, at = null, ops, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [hit, setHit] = useState(0);
  const ref = useRef(null);
  const bodyRef = useRef(null);
  useEscapeLayer(true, () => onClose && onClose());

  useEffect(() => {
    let live = true;
    Promise.resolve(ops.call(`/activities/${activityId}/transcript`))
      .then((d) => live && setData(d))
      .catch((e) => live && setError((e && e.message) || "The transcript didn’t load."));
    return () => {
      live = false;
    };
  }, [activityId, ops]);

  useEffect(() => {
    if (ref.current) ref.current.focus({ preventScroll: true });
  }, []);

  const turns = useMemo(() => (data && data.turns) || [], [data]);
  const target = at === null || at === undefined ? -1 : turnIndexAt(turns, at);
  const hits = useMemo(() => matchingTurns(turns, query), [turns, query]);
  const current = hits.length ? hits[Math.min(hit, hits.length - 1)] : -1;

  // Land on the line asked for, then on each find hit as he steps through them.
  const focusLine = current >= 0 ? current : target;
  useEffect(() => {
    if (focusLine < 0 || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-turn="${focusLine}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center" });
  }, [focusLine, data]);

  const step = (d) => hits.length && setHit((h) => (h + d + hits.length) % hits.length);
  const cover = data && data.coverage;

  return (
    <div className="rec-dialog__backdrop" onClick={onClose}>
      <div className="rec-confirm rec-transcript" role="dialog" aria-modal="true" aria-label="Transcript" tabIndex={-1} ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="rec-transcript__head">
          <div>
            <div className="rec-confirm__title">{data ? data.title : "Transcript"}</div>
            {data && (
              <div className="rec-meta">
                {[shortDate(data.occurred_at), data.minutes ? `${data.minutes} min` : null, cover ? `${cover.words.toLocaleString()} words` : null].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <button type="button" className="rec-close" title="Close (Esc)" aria-label="Close" onClick={onClose}>×</button>
        </div>
        {data && turns.length > 0 && (
          <div className="rec-inline rec-transcript__find">
            <input
              className="rec-input"
              type="search"
              placeholder="Find in the call"
              aria-label="Find in the call"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHit(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  step(e.shiftKey ? -1 : 1);
                }
              }}
            />
            {query.trim() && (
              <>
                <span className="rec-meta">{hits.length ? `${Math.min(hit, hits.length - 1) + 1} of ${hits.length}` : "No match"}</span>
                <button type="button" className="rec-link" disabled={!hits.length} onClick={() => step(-1)} aria-label="Previous match">↑</button>
                <button type="button" className="rec-link" disabled={!hits.length} onClick={() => step(1)} aria-label="Next match">↓</button>
              </>
            )}
          </div>
        )}
        {cover && cover.thin && (
          <div className="rec-transcript__warn">
            Fireflies caught {cover.words.toLocaleString()} words of a {cover.minutes}-minute call, so most of it is missing or garbled. This is usually the wrong transcription language: set it in Fireflies and re-transcribe the call.
          </div>
        )}
        <div className="rec-transcript__body" ref={bodyRef}>
          {!data && !error && <div className="rec-meta">Loading the transcript…</div>}
          {error && <div className="rec-err">{error}</div>}
          {data && turns.length === 0 && <div className="rec-meta">Fireflies has no words for this call.</div>}
          {turns.map((t, i) => {
            const cls = ["rec-transcript__turn", t.ours ? "is-ours" : "", i === target ? "is-target" : "", hits.includes(i) ? "is-hit" : "", i === current ? "is-current" : ""].filter(Boolean).join(" ");
            return (
              <div key={i} className={cls} data-turn={i} dir="auto">
                <span className="rec-transcript__at">{transcriptClock(t.at)}</span>
                <span className="rec-transcript__who">{t.s}</span>
                <span className="rec-transcript__text">{t.t}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
